import { useState } from "react";
import { Accessibility, Smartphone, CheckCircle, AlertTriangle, XCircle, Loader2, Sparkles, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import { EmailResultsButton } from "@/components/EmailResultsButton";

interface EmailContentAnalyzerProps {
  userEmail?: string | null;
  userName?: string | null;
  sessionToken?: string | null;
}

interface Issue {
  severity: "critical" | "major" | "minor";
  issue: string;
  element?: string;
  recommendation: string;
}

interface AnalysisResult {
  overallScore: number;
  accessibilityScore: number;
  mobileScore: number;
  summary: string;
  accessibility: {
    issues: Issue[];
    passed: string[];
  };
  mobile: {
    issues: Issue[];
    passed: string[];
  };
  bestPractices: string[];
}

const sampleEmailHTML = `<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial; margin: 0; padding: 20px; }
    .container { width: 600px; margin: 0 auto; }
    .header { background: #333; padding: 20px; }
    .header h1 { color: #666; font-size: 12px; }
    img { width: 600px; }
    .button { background: #007bff; color: white; padding: 8px 12px; font-size: 11px; }
    .footer { font-size: 9px; color: #999; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>Newsletter</h1>
    </div>
    <img src="banner.jpg">
    <p>Click <a href="#">here</a> to learn more.</p>
    <a href="#" class="button">Buy Now</a>
    <div class="footer">
      <p>Unsubscribe | Privacy Policy</p>
    </div>
  </div>
</body>
</html>`;

export const EmailContentAnalyzer = ({ userEmail, userName, sessionToken }: EmailContentAnalyzerProps) => {
  const [html, setHtml] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<AnalysisResult | null>(null);

  const loadSample = () => {
    setHtml(sampleEmailHTML);
    setResult(null);
  };

  const analyzeContent = async () => {
    if (!html.trim()) {
      toast.error("Please paste email HTML to analyze");
      return;
    }

    setIsAnalyzing(true);
    setResult(null);

    try {
      const { data, error } = await supabase.functions.invoke("analyze-email-content", {
        body: { html: html.trim() },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      setResult(data);
    } catch (error) {
      console.error("Error analyzing content:", error);
      toast.error("Failed to analyze email. Please try again.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-success";
    if (score >= 60) return "text-warning";
    return "text-destructive";
  };

  const getSeverityConfig = (severity: string) => {
    switch (severity) {
      case "critical":
        return { icon: <XCircle className="w-4 h-4" />, color: "text-destructive", bg: "bg-destructive/10 border-destructive/20" };
      case "major":
        return { icon: <AlertTriangle className="w-4 h-4" />, color: "text-warning", bg: "bg-warning/10 border-warning/20" };
      default:
        return { icon: <Info className="w-4 h-4" />, color: "text-blue-500", bg: "bg-blue-500/10 border-blue-500/20" };
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Accessibility className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="font-display text-lg font-semibold text-foreground">
            Email Content Analyzer
          </h3>
          <p className="text-sm text-muted-foreground">
            Check accessibility and mobile-friendliness
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-foreground">
              Email HTML
            </label>
            <Button
              variant="ghost"
              size="sm"
              onClick={loadSample}
              className="text-xs text-primary hover:text-primary/80"
            >
              Load Sample (with issues)
            </Button>
          </div>
          <Textarea
            value={html}
            onChange={(e) => {
              setHtml(e.target.value);
              setResult(null);
            }}
            placeholder="Paste your email HTML here..."
            className="min-h-[160px] font-mono text-xs resize-none"
          />
        </div>

        <Button
          onClick={analyzeContent}
          disabled={isAnalyzing || !html.trim()}
          className="w-full"
        >
          {isAnalyzing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Analyzing Content...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              Analyze for Accessibility & Mobile
            </>
          )}
        </Button>

        {result && (
          <div className="space-y-4 pt-4 border-t border-border">
            {/* Email Results Button */}
            <div className="flex justify-end">
              <EmailResultsButton
                toolId="content-analyzer"
                toolName="Email Content Analyzer"
                results={result}
                userEmail={userEmail || null}
                userName={userName || null}
                sessionToken={sessionToken || null}
              />
            </div>

            {/* Overall Score */}
            <div className="bg-muted/50 rounded-xl p-4">
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground mb-1">Overall</p>
                  <p className={`text-2xl font-bold ${getScoreColor(result.overallScore)}`}>
                    {result.overallScore}
                  </p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Accessibility className="w-3 h-3 text-muted-foreground" />
                    <p className="text-xs text-muted-foreground">Accessibility</p>
                  </div>
                  <p className={`text-2xl font-bold ${getScoreColor(result.accessibilityScore)}`}>
                    {result.accessibilityScore}
                  </p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Smartphone className="w-3 h-3 text-muted-foreground" />
                    <p className="text-xs text-muted-foreground">Mobile</p>
                  </div>
                  <p className={`text-2xl font-bold ${getScoreColor(result.mobileScore)}`}>
                    {result.mobileScore}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Progress value={result.accessibilityScore} className="flex-1 h-2" />
                <Progress value={result.mobileScore} className="flex-1 h-2" />
              </div>
              <p className="text-sm text-muted-foreground mt-3">{result.summary}</p>
            </div>

            {/* Issues Grid - Side by Side on Desktop */}
            <div className="grid lg:grid-cols-2 gap-4">
              {/* Accessibility Issues */}
              {result.accessibility.issues.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                    <Accessibility className="w-4 h-4 text-primary" />
                    Accessibility Issues ({result.accessibility.issues.length})
                  </h4>
                  <div className="space-y-2">
                    {result.accessibility.issues.map((issue, i) => {
                      const config = getSeverityConfig(issue.severity);
                      return (
                        <div key={i} className={`rounded-xl border p-3 ${config.bg}`}>
                          <div className="flex items-start gap-2">
                            <span className={config.color}>{config.icon}</span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`text-xs font-medium uppercase ${config.color}`}>
                                  {issue.severity}
                                </span>
                              </div>
                              <p className="text-sm text-foreground">{issue.issue}</p>
                              {issue.element && (
                                <code className="text-xs bg-background/50 px-1.5 py-0.5 rounded mt-1 inline-block text-muted-foreground">
                                  {issue.element}
                                </code>
                              )}
                              <p className="text-xs text-muted-foreground mt-1.5">
                                💡 {issue.recommendation}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Mobile Issues */}
              {result.mobile.issues.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                    <Smartphone className="w-4 h-4 text-primary" />
                    Mobile-Friendliness Issues ({result.mobile.issues.length})
                  </h4>
                  <div className="space-y-2">
                    {result.mobile.issues.map((issue, i) => {
                      const config = getSeverityConfig(issue.severity);
                      return (
                        <div key={i} className={`rounded-xl border p-3 ${config.bg}`}>
                          <div className="flex items-start gap-2">
                            <span className={config.color}>{config.icon}</span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`text-xs font-medium uppercase ${config.color}`}>
                                  {issue.severity}
                                </span>
                              </div>
                              <p className="text-sm text-foreground">{issue.issue}</p>
                              {issue.element && (
                                <code className="text-xs bg-background/50 px-1.5 py-0.5 rounded mt-1 inline-block text-muted-foreground">
                                  {issue.element}
                                </code>
                              )}
                              <p className="text-xs text-muted-foreground mt-1.5">
                                💡 {issue.recommendation}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Passed Checks */}
            <div className="grid sm:grid-cols-2 gap-4">
              {result.accessibility.passed.length > 0 && (
                <div className="bg-success/5 border border-success/20 rounded-xl p-3">
                  <h4 className="text-xs font-medium text-foreground mb-2 flex items-center gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5 text-success" />
                    Accessibility Passed
                  </h4>
                  <ul className="space-y-1">
                    {result.accessibility.passed.slice(0, 5).map((item, i) => (
                      <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                        <span className="text-success mt-0.5">✓</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {result.mobile.passed.length > 0 && (
                <div className="bg-success/5 border border-success/20 rounded-xl p-3">
                  <h4 className="text-xs font-medium text-foreground mb-2 flex items-center gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5 text-success" />
                    Mobile Passed
                  </h4>
                  <ul className="space-y-1">
                    {result.mobile.passed.slice(0, 5).map((item, i) => (
                      <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                        <span className="text-success mt-0.5">✓</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Best Practices */}
            {result.bestPractices.length > 0 && (
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-3">
                <h4 className="text-xs font-medium text-foreground mb-2 flex items-center gap-1.5">
                  <Info className="w-3.5 h-3.5 text-primary" />
                  Best Practices
                </h4>
                <ul className="space-y-1">
                  {result.bestPractices.map((practice, i) => (
                    <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                      <span className="text-primary mt-0.5">•</span>
                      {practice}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};